import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { useAuth, API } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Loader2, Bell, Trash2, AlertTriangle } from "lucide-react";

export default function CandidatePreferences() {
  const { t } = useI18n();
  const { authHeader, logout } = useAuth();
  const [prefs, setPrefs] = useState({ expected_salary_min: "", expected_salary_max: "", industries: "", target_roles: "", alerts_enabled: true });
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    axios.get(`${API}/candidate/preferences`, { headers: authHeader }).then(r => {
      setPrefs({
        expected_salary_min: r.data.expected_salary_min || "",
        expected_salary_max: r.data.expected_salary_max || "",
        industries: r.data.industries || "",
        target_roles: r.data.target_roles || "",
        alerts_enabled: r.data.alerts_enabled !== false,
      });
    });
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/candidate/preferences`, {
        expected_salary_min: prefs.expected_salary_min ? Number(prefs.expected_salary_min) : null,
        expected_salary_max: prefs.expected_salary_max ? Number(prefs.expected_salary_max) : null,
        industries: prefs.industries,
        target_roles: prefs.target_roles,
        alerts_enabled: prefs.alerts_enabled,
      }, { headers: authHeader });
      toast.success("Preferences saved");
    } catch (e) { toast.error("Save failed"); }
    setLoading(false);
  };

  const deleteAccount = async () => {
    try {
      await axios.delete(`${API}/candidate/account`, { headers: authHeader });
      toast.success("Account deleted");
      logout();
      window.location.href = "/";
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="candidate-prefs">
      <Card className="p-6 space-y-4">
        <h3 className="font-display text-xl font-semibold text-slate-900">Job preferences</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Expected salary min (Rs)</Label>
            <Input data-testid="pref-sal-min" type="number" value={prefs.expected_salary_min} onChange={(e) => setPrefs({ ...prefs, expected_salary_min: e.target.value })} />
          </div>
          <div>
            <Label>Expected salary max (Rs)</Label>
            <Input data-testid="pref-sal-max" type="number" value={prefs.expected_salary_max} onChange={(e) => setPrefs({ ...prefs, expected_salary_max: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Preferred industries</Label>
          <Input data-testid="pref-industries" placeholder="e.g., Tech, Finance, Hospitality" value={prefs.industries} onChange={(e) => setPrefs({ ...prefs, industries: e.target.value })} />
        </div>
        <div>
          <Label>Target roles</Label>
          <Input data-testid="pref-roles" placeholder="e.g., Senior Accountant, Backend Developer" value={prefs.target_roles} onChange={(e) => setPrefs({ ...prefs, target_roles: e.target.value })} />
        </div>
        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-[#1A8FE3]" />
            <div>
              <div className="font-medium text-slate-900">Email job alerts</div>
              <div className="text-xs text-slate-500">Get notified when a job matches your preferences.</div>
            </div>
          </div>
          <Switch data-testid="pref-alerts" checked={prefs.alerts_enabled} onCheckedChange={(v) => setPrefs({ ...prefs, alerts_enabled: v })} />
        </div>
        <Button onClick={save} disabled={loading} data-testid="prefs-save" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save preferences"}
        </Button>
      </Card>

      <Card className="p-6 border-rose-200" data-testid="danger-zone">
        <h3 className="font-display text-xl font-semibold text-rose-700 inline-flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Danger zone</h3>
        <p className="text-sm text-slate-600 mt-2">Permanently delete your account and all associated data (applications, messages, CV).</p>
        {!confirmDelete ? (
          <Button variant="outline" onClick={() => setConfirmDelete(true)} data-testid="delete-account-init" className="mt-4 border-rose-300 text-rose-700 hover:bg-rose-50 rounded-full">
            <Trash2 className="h-4 w-4 mr-2" />Delete my account
          </Button>
        ) : (
          <div className="mt-4 flex gap-3">
            <Button onClick={deleteAccount} data-testid="delete-account-confirm" className="bg-rose-600 hover:bg-rose-700 text-white rounded-full">Yes, permanently delete</Button>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)} className="rounded-full">Cancel</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
